import type { FleetPage } from "./navigation-routes";

// The Fleet section's pages and the labels the shell knows them by.
const FLEET_PAGE_LABELS: Record<string, string> = {
  repositories: "Repositories",
  agents: "Fleet Agents",
  inspections: "Inspect",
};
const FLEET_PAGES_BY_LABEL: Record<string, FleetPage> = {
  Repositories: "repositories",
  "Fleet Agents": "agents",
  Inspect: "inspections",
};

/** The estate and fleet a Fleet-scoped URL names, if it names them. */
export function fleetScopeInPath(
  pathname: string,
): { estateId: string; fleetId: string } | null {
  const match = pathname.match(/^\/estate\/([^/]+)\/fleet\/([^/]+)\//);
  return match
    ? {
        estateId: decodeURIComponent(match[1]),
        fleetId: decodeURIComponent(match[2]),
      }
    : null;
}

/** The Fleet-scoped page a nav label stands for, for the labels that have one. */
export function fleetPageForLabel(label: string): FleetPage | null {
  return FLEET_PAGES_BY_LABEL[label] ?? null;
}

const SCOPE = "/estate/[^/]+/fleet/[^/]+";

/**
 * The nav label a Fleet-scoped URL stands for, detail pages included. Ordered
 * longest first: a scan path also matches the repository pattern's prefix, and
 * "upload" would otherwise read as a repository id.
 */
export function fleetPageInPath(pathname: string): string | null {
  const scan = pathname.match(
    new RegExp(`^${SCOPE}/repositories/([^/]+)/scans/([^/]+)$`),
  );
  if (scan) return `scan:${decodeURIComponent(scan[2])}`;

  const task = pathname.match(new RegExp(`^${SCOPE}/tasks/([^/]+)$`));
  if (task) return `task:${decodeURIComponent(task[1])}`;

  const upload = pathname.match(new RegExp(`^${SCOPE}/repositories/upload$`));
  if (upload) return "Upload Archive";

  // The add page is the finder above a Fleet's repositories now; a link to
  // the old one still lands there.
  const add = pathname.match(new RegExp(`^${SCOPE}/repositories/add$`));
  if (add) return "Repositories";

  const repository = pathname.match(
    new RegExp(`^${SCOPE}/repositories/([^/]+)$`),
  );
  if (repository) return `repository:${decodeURIComponent(repository[1])}`;

  const page = pathname.match(
    new RegExp(`^${SCOPE}/(repositories|agents|inspections)$`),
  );
  return page ? FLEET_PAGE_LABELS[page[1]] : null;
}

/**
 * The repository a retired, unscoped URL was about, so the redirect can name
 * the Fleet that repository is filed in rather than whichever one is selected.
 * A scan URL that names no repository answers null: only the scan is known,
 * and what it belongs to has to be looked up.
 */
export function retiredRepositoryInPath(pathname: string): string | null {
  const match =
    pathname.match(/^\/repositories\/([^/]+)\/scans\/[^/]+$/) ??
    pathname.match(/^\/repositories\/([^/]+)$/);
  const repoId = match ? decodeURIComponent(match[1]) : null;
  return repoId === "upload" || repoId === "overview" ? null : repoId;
}

/**
 * What a retired, unscoped address resolves to. Kept resolvable rather than
 * removed so a link already sent opens something the shell can re-point; every
 * one of these answers the same label its scoped replacement does.
 */
export function retiredRouteInPath(
  pathname: string,
): { label: string; section: "Fleet" } | null {
  const label = (value: string) => ({
    label: value,
    section: "Fleet" as const,
  });
  const task = pathname.match(/^\/tasks\/([0-9a-f-]{36})$/i);
  if (task) return label(`task:${task[1]}`);
  const scan = pathname.match(/^\/repositories\/[^/]+\/scans\/([^/]+)$/);
  if (scan) return label(`scan:${decodeURIComponent(scan[1])}`);
  const scanOnly = pathname.match(/^\/repositories\/scans\/([^/]+)$/);
  if (scanOnly) return label(`scan:${decodeURIComponent(scanOnly[1])}`);
  const repository = pathname.match(/^\/repositories\/([^/]+)$/);
  if (repository)
    return label(`repository:${decodeURIComponent(repository[1])}`);
  if (
    pathname === "/repositories/overview" ||
    pathname === "/maintenance/find-new"
  )
    return label("Inspect");
  return null;
}
